import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { KpiCard, QuotaBar, SearchField } from '../../../components/design-system/admin';
import { CrmInvestor, INVESTOR_STAGES, INVESTOR_TYPES, RAISE_TARGET, parseMoney } from '../../../common/models/crm.model';
import { CrmBoard } from './crm-board';
import { CrmCardBody } from './crm-card';
import { CrmField, CrmRecordSheet } from './crm-record-sheet';
import { useCrmBoard } from './use-crm-board';

const INVESTOR_FIELDS: CrmField[] = [
  { key: 'name', label: 'ADMIN_CRM.FIELDS.INVESTOR_NAME' },
  { key: 'type', label: 'ADMIN_CRM.FIELDS.TYPE', type: 'select', options: INVESTOR_TYPES },
  { key: 'contact', label: 'ADMIN_CRM.FIELDS.INVESTOR_CONTACT' },
  { key: 'email', label: 'ADMIN_CRM.FIELDS.EMAIL' },
  { key: 'country', label: 'ADMIN_CRM.FIELDS.COUNTRY', type: 'country' },
  { key: 'stage', label: 'ADMIN_CRM.FIELDS.STAGE', type: 'select', options: Object.fromEntries(INVESTOR_STAGES.map((s) => [s.id, s.label])) },
  { key: 'ticket', label: 'ADMIN_CRM.FIELDS.TICKET', placeholder: 'ADMIN_CRM.PLACEHOLDERS.TICKET' },
  { key: 'amount', label: 'ADMIN_CRM.FIELDS.AMOUNT', placeholder: 'ADMIN_CRM.PLACEHOLDERS.AMOUNT' },
  { key: 'focus', label: 'ADMIN_CRM.FIELDS.FOCUS', full: true },
  { key: 'source', label: 'ADMIN_CRM.FIELDS.INTRO', full: true },
  { key: 'msft', label: 'ADMIN_CRM.FIELDS.MSFT', full: true },
  { key: 'next', label: 'ADMIN_CRM.FIELDS.NEXT' },
  { key: 'due', label: 'ADMIN_CRM.FIELDS.DUE', type: 'date' },
  { key: 'tags', label: 'ADMIN_CRM.FIELDS.TAGS_INVESTORS', full: true },
  { key: 'notes', label: 'ADMIN_CRM.FIELDS.NOTES_HISTORY', type: 'textarea' },
];

// Raw amount (tickets may be EUR or USD; the round target is a single figure, so progress is tracked on numbers).
const amount = (v?: string) => parseMoney(v)?.n || 0;
const usd = (n: number) => `$${n.toLocaleString('en-US')}`;

/** Fundraising pipeline: progress against the round target, then the investors Kanban. */
export const CrmInvestorsTab: React.FC = () => {
  const { t } = useTranslation();
  const board = useCrmBoard<CrmInvestor>('investors', 'ADMIN_CRM.INVESTORS.SAVED');
  const investors = board.items;
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [editing, setEditing] = useState<Partial<CrmInvestor> | null>(null);

  const shown = useMemo(() => {
    const needle = q.toLowerCase();
    return investors
      .filter((i) => !type || i.type === type)
      .filter((i) => !needle || JSON.stringify(i).toLowerCase().includes(needle));
  }, [investors, q, type]);

  const committed = investors.filter((i) => i.stage === 'committed').reduce((s, i) => s + amount(i.amount), 0);
  const live = investors.filter((i) => ['meeting', 'diligence', 'termsheet'].includes(i.stage)).reduce((s, i) => s + amount(i.amount), 0);
  const active = investors.filter((i) => i.stage !== 'passed').length;
  const pct = Math.min(100, Math.round((committed / RAISE_TARGET) * 100));

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard loading={board.loading} label={t('ADMIN_CRM.INVESTORS.KPI.TARGET')} value={`$${RAISE_TARGET / 1000}k`} hint={t('ADMIN_CRM.INVESTORS.KPI.TARGET_HINT')} />
        <KpiCard loading={board.loading} label={t('ADMIN_CRM.INVESTORS.KPI.COMMITTED')} value={<span className="text-success">{usd(committed)}</span>} hint={t('ADMIN_CRM.INVESTORS.KPI.COMMITTED_HINT', { pct })} />
        <KpiCard loading={board.loading} label={t('ADMIN_CRM.INVESTORS.KPI.LIVE')} value={<span className="text-warning">{usd(live)}</span>} hint={t('ADMIN_CRM.INVESTORS.KPI.LIVE_HINT')} />
        <KpiCard loading={board.loading} label={t('ADMIN_CRM.INVESTORS.KPI.ACTIVE')} value={active} hint={t('ADMIN_CRM.INVESTORS.KPI.ACTIVE_HINT')} />
      </div>
      <QuotaBar used={committed} limit={RAISE_TARGET} />

      <div className="flex flex-wrap items-center gap-2">
        <SearchField value={q} onChange={setQ} placeholder={t('ADMIN_CRM.INVESTORS.SEARCH')} className="w-full sm:w-80" />
        <NativeSelect value={type} onChange={(e) => setType(e.target.value)} aria-label={t('ADMIN_CRM.COMMON.TYPE')}>
          <NativeSelectOption value="">{t('ADMIN_CRM.COMMON.ALL_TYPES')}</NativeSelectOption>
          {Object.entries(INVESTOR_TYPES).map(([k, v]) => <NativeSelectOption key={k} value={k}>{t(v)}</NativeSelectOption>)}
        </NativeSelect>
        <span className="flex-1" />
        <Button onClick={() => setEditing({ stage: 'sourced', type: 'angel' })}><Plus />{t('ADMIN_CRM.INVESTORS.NEW')}</Button>
      </div>

      {!board.loading && (
        <CrmBoard<CrmInvestor>
          stages={INVESTOR_STAGES}
          items={shown}
          manualOrder
          onOpen={setEditing}
          onMoveStage={board.moveStage}
          onReorder={board.reorder}
          columnExtra={(col) => {
            const sum = col.reduce((s, i) => s + amount(i.amount), 0);
            return sum ? `≈${usd(sum)}` : null;
          }}
          renderCard={(i) => (
            <CrmCardBody country={i.country} title={i.name} next={i.next} due={i.due} tags={i.tags}>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {INVESTOR_TYPES[i.type] ? t(INVESTOR_TYPES[i.type]) : i.type}{i.contact ? ` · ${i.contact}` : ''}
              </p>
              {(i.amount || i.ticket) && (
                <p className="mt-1 text-xs font-medium">{i.amount ? usd(amount(i.amount)) : i.ticket}</p>
              )}
              {/oui|yes/i.test(i.msft || '') && <Badge variant="secondary" className="mt-1.5 px-1.5 text-[10px]">{t('ADMIN_CRM.INVESTORS.MS_CODE')}</Badge>}
            </CrmCardBody>
          )}
        />
      )}

      <CrmRecordSheet<CrmInvestor>
        record={editing}
        fields={INVESTOR_FIELDS}
        required="name"
        title={{ create: t('ADMIN_CRM.INVESTORS.NEW'), edit: t('ADMIN_CRM.INVESTORS.EDIT') }}
        onSave={async (d) => { if (await board.save(d)) setEditing(null); }}
        onDelete={async (id) => { if (await board.remove(id)) setEditing(null); }}
        onClose={() => setEditing(null)}
      />
    </div>
  );
};
